import { useRef, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SliderStateOptions, useSliderState } from '@react-stately/slider';
import { useNumberFormatter } from '@react-aria/i18n';
import { useSlider } from '@react-aria/slider';
import { DEFAULT_ARDOQ_I18NLOCALE, t } from '@ardoq/i18n';
import {
  InfinitySymbol,
  SliderBar,
  SliderBarWrapper,
  SliderValue,
  SliderValueBar,
  SliderWrapper,
} from './atoms';
import { SliderThumb } from './SliderThumb';
import { SliderProps } from './types';
import { isNumber } from 'lodash';

const AllocateWidth = styled.div`
  visibility: hidden;
  height: 0;
`;

export const SliderInput = ({
  hasError,
  value,
  defaultValue,
  isValueLabelVisible = true,
  isUnlimited,
  thumbColor,
  barColor,
  valueBarColor,
  substituteMax,
  substituteMaxLabel,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...props
}: SliderProps) => {
  const trackRef = useRef(null);
  const numberFormatter = useNumberFormatter();
  const [sliderValue, setSliderValue] = useState(
    isNumber(value)
      ? substituteMax && value === substituteMax
        ? props.maxValue
        : value
      : undefined
  );
  const computedProps = useMemo<SliderStateOptions<number[]>>(
    () => ({
      minValue: 0,
      step: 1,
      ...props,
      onChange: ([newValue]) => {
        setSliderValue(newValue);
        props.onChange?.(newValue);
      },
      onChangeEnd: ([newValue]) => props.onChangeEnd?.(newValue),
      value: isNumber(sliderValue) ? [sliderValue] : undefined,
      defaultValue: isNumber(defaultValue) ? [defaultValue] : undefined,
      numberFormatter,
      // Added for aria-label related warnings
      label:
        props.label || t(i18nLocale, 'forms:slider.fallbackLabel', 'slider'),
    }),
    [
      sliderValue,
      setSliderValue,
      defaultValue,
      numberFormatter,
      props,
      i18nLocale,
    ]
  );
  const state = useSliderState(computedProps);
  const { groupProps, trackProps } = useSlider(computedProps, state, trackRef);
  const thumbValueLabel = state.getThumbValueLabel(0);
  const isMaxValueSelected = state.getThumbValue(0) === props.maxValue;
  const percentValue = Math.min(state.getThumbPercent(0) * 100, 100);

  return (
    <SliderWrapper {...groupProps}>
      <SliderBarWrapper>
        <SliderBar
          {...trackProps}
          ref={trackRef}
          $barColor={barColor}
          aria-label={t(
            i18nLocale,
            'forms:slider.trackAriaLabel',
            'slider-track'
          )}
        />
        <SliderValueBar
          style={{ width: `${percentValue}%` }}
          $hasError={hasError}
          $valueBarColor={valueBarColor}
        />
        <SliderThumb
          index={0}
          state={state}
          percentValue={percentValue}
          trackRef={trackRef}
          hasError={hasError}
          thumbColor={thumbColor}
        />
      </SliderBarWrapper>
      {isValueLabelVisible && (
        <SliderValue
          aria-label={t(
            i18nLocale,
            'forms:slider.valueAriaLabel',
            'slider-value'
          )}
        >
          {isMaxValueSelected
            ? substituteMaxLabel ||
              (isUnlimited ? (
                <InfinitySymbol />
              ) : (
                (substituteMax ?? thumbValueLabel)
              ))
            : thumbValueLabel}
          {
            /* allocate width for the substituteMaxLabel by always rendering it hidden */
            substituteMaxLabel && (
              <AllocateWidth>{substituteMaxLabel}</AllocateWidth>
            )
          }
        </SliderValue>
      )}
    </SliderWrapper>
  );
};
